import { parseTaskInstance, parseTaskTombstone, type TaskInstance, type TaskTombstone } from './taskSchemas';

export type TaskDumpMerge =
  | { outcome: 'merged'; tasks: TaskInstance[]; changed: boolean }
  | { outcome: 'refetch'; tasks: TaskInstance[]; changed: false };

type TaskChange = TaskInstance | TaskTombstone;

const seriesKey = (task: TaskInstance): string =>
  JSON.stringify([task.taskKey, task.subject?.type ?? null, task.subject?.id ?? null]);

/** Reconciles complete resources into the shared collection without applying presentation filters. */
export const mergeTaskDump = (
  current: readonly TaskInstance[],
  resources: readonly unknown[],
  { allowEqualVersion = false }: { allowEqualVersion?: boolean } = {},
): TaskDumpMerge => {
  const incoming = resources.flatMap<TaskChange>((resource) => {
    const tombstone = parseTaskTombstone(resource);
    if (tombstone) return [tombstone];

    const task = parseTaskInstance(resource);
    return task ? [task] : [];
  });
  if (resources.length === 0 || incoming.length !== resources.length) {
    return { outcome: 'refetch', tasks: [...current], changed: false };
  }

  const merged = [...current];
  let changed = false;
  let expectedCatalogRevision = current[0]?.catalogRevision;
  for (const change of incoming) {
    const instanceIndex = merged.findIndex((held) => held.id === change.id);
    const instance = instanceIndex >= 0 ? merged[instanceIndex] : undefined;

    if ('isDeleted' in change) {
      if (instance && change.dbVersion >= instance.dbVersion) {
        merged.splice(instanceIndex, 1);
        changed = true;
      }
      continue;
    }

    const task = change;

    if (expectedCatalogRevision !== undefined && task.catalogRevision !== expectedCatalogRevision) {
      return { outcome: 'refetch', tasks: [...current], changed: false };
    }
    expectedCatalogRevision ??= task.catalogRevision;

    if (instance) {
      if (task.dbVersion > instance.dbVersion || (allowEqualVersion && task.dbVersion === instance.dbVersion)) {
        merged[instanceIndex] = task;
        changed = true;
      }
      continue;
    }

    const incomingSeriesKey = seriesKey(task);
    const matchingSeries = merged
      .map((held, index) => ({ held, index }))
      .filter(({ held }) => seriesKey(held) === incomingSeriesKey);
    if (matchingSeries.length > 1) return { outcome: 'refetch', tasks: [...current], changed: false };

    const series = matchingSeries[0];
    if (!series) {
      merged.push(task);
      changed = true;
      continue;
    }

    const incomingCreatedAt = Date.parse(task.createdAt);
    const heldCreatedAt = Date.parse(series.held.createdAt);
    if (Number.isNaN(incomingCreatedAt) || Number.isNaN(heldCreatedAt) || incomingCreatedAt === heldCreatedAt) {
      return { outcome: 'refetch', tasks: [...current], changed: false };
    }
    if (incomingCreatedAt > heldCreatedAt) {
      merged[series.index] = task;
      changed = true;
    }
  }

  return { outcome: 'merged', tasks: merged, changed };
};
